/**
 * ResidentProfileOverview.jsx
 * --------------------------------------------------
 * - Self-contained Save button (runs its own mutation)
 * - Prevents "save then reverts to old" by NOT reloading form on mode change
 * - Forces Apollo watched queries to refetch after save (fixes stale parent props)
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Divider,
  Box,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useReactToPrint } from "react-to-print";

/* ================= GRAPHQL ================= */
const READ_ONLY_NOTICE_EVENT = "resident-profile-readonly-notice";

const RESIDENT_FIELDS = gql`
  fragment ResidentFields on Resident {
    __typename
    id
    businessId

    firstName
    lastName
    dateOfBirth
    gender
    language
    admissionDate
    dischargeDate
    roomNumber
    status

    primaryPayor
    insuranceType
    medicaidId
    medicareNumber
    mcoName
    caseManagerName
    caseManagerPhone
    caseManagerAgency
    socialWorkerName
    socialWorkerPhone
    socialWorkerAgency

    pcpName
    pcpPhone
    preferredHospital
    pharmacyName
    pharmacyPhone

    emergencyContactName
    emergencyContactPhone
    familyContactName
    familyContactRelationship
    familyContactPhone
    poaName
    poaPhone
    poaType

    ssn
  }
`;

const UPDATE_RESIDENT = gql`
  mutation UpdateResident($id: ID!, $data: ResidentInput!) {
    updateResident(id: $id, data: $data) {
      ...ResidentFields
    }
  }
  ${RESIDENT_FIELDS}
`;

/* ================= UI HELPERS ================= */

const toCommaSeparated = (value) =>
  Array.isArray(value) ? value.filter(Boolean).join(", ") : value || "";

const Section = ({ title, children }) => (
  <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const Field = ({
  label,
  value,
  name,
  editable,
  onChange,
  type = "text",
  onBlockedAttempt,
  blockedMessage,
  blockOnReadOnly = false,
}) => {
  const isReadOnly = !editable;
  const shouldBlock = isReadOnly && (blockOnReadOnly || !!name);

  const triggerBlockedAttempt = () => {
    if (!shouldBlock) return;
    const message =
      blockedMessage ||
      "This section is read-only. Click Edit to make changes.";
    if (onBlockedAttempt) {
      onBlockedAttempt(message);
      return;
    }
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(
        new CustomEvent(READ_ONLY_NOTICE_EVENT, {
          detail: { message },
        })
      );
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={type}
      value={value ?? ""}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      InputProps={{
        readOnly: isReadOnly,
        inputProps: {
          tabIndex: isReadOnly ? -1 : 0,
        },
      }}
      onMouseDown={(e) => {
        if (!shouldBlock) return;
        e.preventDefault();
        triggerBlockedAttempt();
      }}
      onFocus={(e) => {
        if (!shouldBlock) return;
        e.target.blur();
      }}
      onKeyDown={(e) => {
        if (!shouldBlock) return;
        const allowed = ["Tab", "Shift", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
        if (allowed.includes(e.key)) return;
        e.preventDefault();
        triggerBlockedAttempt();
      }}
      sx={
        isReadOnly
          ? {
            "& .MuiInputBase-input": { cursor: "not-allowed" },
          }
          : undefined
      }
      onChange={(e) => {
        if (isReadOnly) return;
        onChange?.(name, e.target.value);
      }}
    />
  );
};

/* ================= INPUT SHAPE ================= */

const RESIDENT_INPUT_KEYS = [
  "businessId",

  "firstName",
  "lastName",
  "dateOfBirth",
  "gender",
  "language",
  "admissionDate",
  "dischargeDate",
  "roomNumber",
  "status",

  "primaryPayor",
  "insuranceType",
  "medicaidId",
  "medicareNumber",
  "mcoName",
  "caseManagerName",
  "caseManagerPhone",
  "caseManagerAgency",
  "socialWorkerName",
  "socialWorkerPhone",
  "socialWorkerAgency",

  "pcpName",
  "pcpPhone",
  "preferredHospital",
  "pharmacyName",
  "pharmacyPhone",

  "emergencyContactName",
  "emergencyContactPhone",
  "familyContactName",
  "familyContactRelationship",
  "familyContactPhone",
  "poaName",
  "poaPhone",
  "poaType",

  "ssn",
];

const EMPTY_FORM = RESIDENT_INPUT_KEYS.reduce((acc, k) => {
  acc[k] = "";
  return acc;
}, {});

function emptyToNull(v) {
  return v === "" ? null : v;
}

function resolveBusinessId({ businessIdProp, resident, form }) {
  return (
    businessIdProp ||
    form?.businessId ||
    resident?.businessId ||
    resident?.business?.id ||
    ""
  );
}

/**
 * Build ResidentInput payload:
 * - whitelist keys
 * - convert "" to null (except businessId)
 * - normalize status to number
 */
function buildResidentInput({ form, resident, businessIdProp }) {
  const data = {};

  for (const key of RESIDENT_INPUT_KEYS) {
    data[key] = form?.[key] ?? "";
  }

  data.businessId = resolveBusinessId({ businessIdProp, resident, form });

  Object.keys(data).forEach((k) => {
    if (k === "businessId") return;
    data[k] = emptyToNull(data[k]);
  });

  if (data.status !== null && data.status !== undefined) {
    const n = Number(data.status);
    data.status = Number.isNaN(n) ? data.status : n;
  }

  return data;
}

/* ================= COMPONENT ================= */

export default function ResidentProfileOverview({ resident, businessId }) {
  const client = useApolloClient();
  const printRef = useRef();

  const [mode, setMode] = useState("view"); // view | edit
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [dirty, setDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [editNotice, setEditNotice] = useState({
    open: false,
    message: "",
    nonce: 0,
  });

  const [updateResident] = useMutation(UPDATE_RESIDENT, {
    /**
     * Cache update: write the returned Resident into cache.
     * This is safer than cache.modify for whole-object replacement.
     */
    update(cache, { data }) {
      const updated = data?.updateResident;
      if (!updated?.id) return;

      const cacheId = cache.identify({
        __typename: "Resident",
        id: updated.id,
      });

      if (!cacheId) return;

      cache.writeFragment({
        id: cacheId,
        fragment: RESIDENT_FIELDS,
        data: updated,
      });
    },
  });

  /**
   * IMPORTANT FIX:
   * Load from resident when switching resident ids (or business context).
   * We intentionally do NOT depend on mode/form object here, so save->view
   * won't overwrite from stale parent props.
   * If user switches resident during edit, force view mode and load the newly
   * selected resident to avoid accidental save against wrong profile.
   */
  useEffect(() => {
    if (!resident?.id) return;

    const nextForm = { ...EMPTY_FORM };
    for (const k of RESIDENT_INPUT_KEYS) {
      nextForm[k] = resident?.[k] ?? "";
    }

    nextForm.businessId = resolveBusinessId({
      businessIdProp: businessId,
      resident,
      form: nextForm,
    });

    setForm(nextForm);
    setMode("view");
    setDirty(false);
    setSaveError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resident?.id, businessId]); // <-- mode and resident object intentionally NOT here

  const age = useMemo(() => {
    if (!form.dateOfBirth) return "";
    const dob = new Date(form.dateOfBirth);
    if (Number.isNaN(dob.getTime())) return "";
    return Math.floor(
      (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    );
  }, [form.dateOfBirth]);

  const isEdit = mode === "edit";
  const medicalSummary = resident?.carePlan?.medicalSummary || resident?.carePlan || {};
  const residentDisplayName =
    [form.firstName, form.lastName].filter(Boolean).join(" ").trim() ||
    `Resident #${resident?.id || ""}`;
  const printedAt = new Date().toLocaleString();

  const handleChange = (field, value) => {
    if (!field) return;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleCancel = () => {
    const nextForm = { ...EMPTY_FORM };
    for (const k of RESIDENT_INPUT_KEYS) {
      nextForm[k] = resident?.[k] ?? "";
    }
    nextForm.businessId = resolveBusinessId({
      businessIdProp: businessId,
      resident,
      form: nextForm,
    });

    setForm(nextForm);
    setMode("view");
    setDirty(false);
    setSaveError("");
  };

  const handleSave = async () => {
    if (!resident?.id) {
      setSaveError("Missing resident id. Cannot save.");
      return;
    }
    if (!dirty || saving) return;

    const resolvedBusinessId = resolveBusinessId({
      businessIdProp: businessId,
      resident,
      form,
    });

    if (!resolvedBusinessId) {
      setSaveError(
        "Missing businessId. Your API requires businessId in ResidentInput."
      );
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      const data = buildResidentInput({
        form,
        resident,
        businessIdProp: businessId,
      });

      const res = await updateResident({
        variables: { id: resident.id, data },

        /**
         * If parent props are stale because parent stores resident in state,
         * this forces all active watched queries to refresh.
         * This is the most reliable "no parent changes" fix.
         */
      });

      const updated = res?.data?.updateResident;

      // Keep THIS component correct immediately
      if (updated?.id) {
        const nextForm = { ...EMPTY_FORM };
        for (const k of RESIDENT_INPUT_KEYS) {
          nextForm[k] = updated?.[k] ?? "";
        }
        nextForm.businessId = resolveBusinessId({
          businessIdProp: businessId,
          resident: updated,
          form: nextForm,
        });
        setForm(nextForm);
      }

      // Force parent and lists to refresh (fixes "old until hard refresh")
      await client.refetchQueries({ include: "active" });

      setMode("view");
      setDirty(false);
    } catch (err) {
      setSaveError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Resident_Face_Sheet_${resident?.lastName || ""}`,
  });
  const showReadOnlyNotice = (message) => {
    setEditNotice({
      open: true,
      message: message || "This section is read-only. Click Edit to make changes.",
      nonce: Date.now(),
    });
  };

  useEffect(() => {
    const handleReadOnlyNotice = (event) => {
      const msg =
        event?.detail?.message ||
        "This section is read-only. Click Edit to make changes.";
      showReadOnlyNotice(msg);
    };
    if (typeof window !== "undefined") {
      window.addEventListener(READ_ONLY_NOTICE_EVENT, handleReadOnlyNotice);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(READ_ONLY_NOTICE_EVENT, handleReadOnlyNotice);
      }
    };
  }, []);

  if (!resident) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Resident Face Sheet
          </Typography>
          {saveError ? (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {saveError}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>

          {!isEdit && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setMode("edit")}
            >
              Edit
            </Button>
          )}

          {isEdit && (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                disabled={saving}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Box ref={printRef}>
        <Box
          className="print-fixed-header"
          sx={{
            display: "none",
            "@media print": {
              display: "block",
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Form: Resident Face Sheet | Resident: {residentDisplayName}
            </Typography>
            <Typography variant="caption">Printed: {printedAt}</Typography>
          </Stack>
        </Box>
        <Box className="print-content-offset">
        <Section title="Identity & Admission">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Field
                label="First Name"
                value={form.firstName}
                editable={isEdit}
                name="firstName"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Last Name"
                value={form.lastName}
                editable={isEdit}
                name="lastName"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field label="Age" value={age} editable={false} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Field
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth}
                editable={isEdit}
                name="dateOfBirth"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Gender"
                value={form.gender}
                editable={isEdit}
                name="gender"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Language"
                value={form.language}
                editable={isEdit}
                name="language"
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Field
                label="Admission Date"
                type="date"
                value={form.admissionDate}
                editable={isEdit}
                name="admissionDate"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Discharge Date"
                type="date"
                value={form.dischargeDate}
                editable={isEdit}
                name="dischargeDate"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Field
                label="Room / Bed"
                value={form.roomNumber}
                editable={isEdit}
                name="roomNumber"
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Field
                label="Status"
                value={form.status}
                editable={isEdit}
                name="status"
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Insurance & Case Management">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><Field label="Primary Payor" value={form.primaryPayor} editable={isEdit} name="primaryPayor" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Insurance Type" value={form.insuranceType} editable={isEdit} name="insuranceType" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Medicaid ID" value={form.medicaidId} editable={isEdit} name="medicaidId" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="Medicare #" value={form.medicareNumber} editable={isEdit} name="medicareNumber" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Managed Care Org (MCO)" value={form.mcoName} editable={isEdit} name="mcoName" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="Case Manager Name" value={form.caseManagerName} editable={isEdit} name="caseManagerName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Case Manager Phone" value={form.caseManagerPhone} editable={isEdit} name="caseManagerPhone" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Case Manager Agency" value={form.caseManagerAgency} editable={isEdit} name="caseManagerAgency" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="Social Worker Name" value={form.socialWorkerName} editable={isEdit} name="socialWorkerName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Social Worker Phone" value={form.socialWorkerPhone} editable={isEdit} name="socialWorkerPhone" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Social Worker Agency" value={form.socialWorkerAgency} editable={isEdit} name="socialWorkerAgency" onChange={handleChange} /></Grid>
          </Grid>
        </Section>

        <Section title="Medical Providers">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><Field label="Primary Care Provider" value={form.pcpName} editable={isEdit} name="pcpName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="PCP Phone" value={form.pcpPhone} editable={isEdit} name="pcpPhone" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Preferred Hospital" value={form.preferredHospital} editable={isEdit} name="preferredHospital" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="Pharmacy Name" value={form.pharmacyName} editable={isEdit} name="pharmacyName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Pharmacy Phone" value={form.pharmacyPhone} editable={isEdit} name="pharmacyPhone" onChange={handleChange} /></Grid>
          </Grid>
        </Section>

        <Section title="Medical Summary (Update this in Preliminary Service Plan)">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Field
                label="Primary Diagnosis"
                value={medicalSummary.primaryDiagnosis}
                editable={false}
                blockOnReadOnly
                blockedMessage="This value is managed in Preliminary Service Plan. Open Preliminary Service Plan to update it."
                onBlockedAttempt={showReadOnlyNotice}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                label="Special Diet"
                value={medicalSummary.specialDiet}
                editable={false}
                blockOnReadOnly
                blockedMessage="This value is managed in Preliminary Service Plan. Open Preliminary Service Plan to update it."
                onBlockedAttempt={showReadOnlyNotice}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                label="Other Diagnoses"
                value={toCommaSeparated(medicalSummary.secondaryDiagnoses)}
                editable={false}
                blockOnReadOnly
                blockedMessage="This value is managed in Preliminary Service Plan. Open Preliminary Service Plan to update it."
                onBlockedAttempt={showReadOnlyNotice}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                label="Allergies"
                value={toCommaSeparated(medicalSummary.allergies)}
                editable={false}
                blockOnReadOnly
                blockedMessage="This value is managed in Preliminary Service Plan. Open Preliminary Service Plan to update it."
                onBlockedAttempt={showReadOnlyNotice}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Emergency, Family & Legal Contacts">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><Field label="Emergency Contact Name" value={form.emergencyContactName} editable={isEdit} name="emergencyContactName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Emergency Phone" value={form.emergencyContactPhone} editable={isEdit} name="emergencyContactPhone" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="Family Contact Name" value={form.familyContactName} editable={isEdit} name="familyContactName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Family Relationship" value={form.familyContactRelationship} editable={isEdit} name="familyContactRelationship" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="Family Phone" value={form.familyContactPhone} editable={isEdit} name="familyContactPhone" onChange={handleChange} /></Grid>

            <Grid item xs={12} md={4}><Field label="POA / Guardian Name" value={form.poaName} editable={isEdit} name="poaName" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="POA Phone" value={form.poaPhone} editable={isEdit} name="poaPhone" onChange={handleChange} /></Grid>
            <Grid item xs={12} md={4}><Field label="POA Type" value={form.poaType} editable={isEdit} name="poaType" onChange={handleChange} /></Grid>
          </Grid>
        </Section>

        {/* SSN intentionally hidden from resident entry/profile UI for now. */}
        </Box>
      </Box>
      <Snackbar
        key={editNotice.nonce}
        open={editNotice.open}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setEditNotice((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setEditNotice((prev) => ({ ...prev, open: false }))}
        >
          {editNotice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
